/**
 * @fileoverview Choice question component for rendering radio button questions
 *
 * @example
 * // Usage in component:
 * import { ChoiceQuestion } from './Choice'
 *
 * <ChoiceQuestion onSubmit={onSubmit} question={question} />
 */

import { useState } from 'react'
import { NavigationButton } from '../NavigationButton'

import { DesktopProgress, MobileProgress } from '../Progress/Progress'
import { Text } from '../typography/Typography'
import css from './Choice.module.css'
import { Instructions } from './sections/Instructions'
import { Title } from './sections/Title'

/**
 * @function classNames
 * @description Helper function to join class names with spaces
 * @param {...string[]} classes - The class names to join
 * @returns {string} The joined class names
 */
function classNames(...classes: string[]) {
    return classes.filter(Boolean).join(' ')
}

/**
 * @function ChoiceQuestion
 * @description Component for rendering choice question with radio buttons and navigation
 * @param {Novoic.ChoiceQuestionProps} props - The props for the component
 * @returns {JSX.Element} The ChoiceQuestion component
 */
export function ChoiceQuestion({ onSubmit, question }: Novoic.ChoiceQuestionProps) {
    const { id, question: body } = question
    const { title, accent, instructions, subtext, choices, button } = body

    const [errors, setErrors] = useState<Record<string, unknown>>({})
    const [state, setState] = useState(
        choices.map((choice) => ({
            ...choice,
            value: choice.default,
            pristine: true,
        }))
    )

    async function onSubmitForm() {
        const errors = await onSubmit(id, {
            choices: state.reduce((acc, radio) => {
                acc[radio.name] = radio.value
                return acc
            }, {} as Record<string, string | number>),
        })

        if (errors && Object.keys(errors).length) {
            setErrors(errors)
        }
    }

    function onChange(name: string, value: string | number) {
        const newState = state.map((radio) => {
            if (radio.name === name) {
                radio.value = value
                radio.pristine = false
            }

            return radio
        })

        delete errors[name]
        setErrors(errors)
        setState(newState)
    }

    return (
        <div className={css.container}>
            <MobileProgress />
            <Title accent={accent}>{title}</Title>
            {subtext && <Text>{subtext}</Text>}
            {instructions && <Instructions instructions={instructions} />}

            <div className={css.choice_container}>
                {state.map((radio) => (
                    <div key={radio.name} className={css.item}>
                        <Text>{radio.title}</Text>
                        <div className={css.horizontal}>
                            <div
                                className={classNames(
                                    css.radio_container,
                                    errors[radio.name] ? css.error : css.normal
                                )}
                            >
                                {radio.options.map((option) => (
                                    <button
                                        className={css.button}
                                        key={option.title}
                                        onClick={() => onChange(radio.name, option.value)}
                                    >
                                        <input
                                            id={radio.name + option.title}
                                            className={css.radio_input}
                                            type="radio"
                                            value={option.value}
                                            checked={radio.value === option.value}
                                            name={radio.name}
                                            onChange={() => onChange(radio.name, option.value)}
                                        />
                                        <label htmlFor={radio.name + option.title}>
                                            <Text>{option.title}</Text>
                                        </label>
                                    </button>
                                ))}
                            </div>
                            <div className={css.spacer}></div>
                        </div>
                    </div>
                ))}
            </div>

            <div className={css.button_container}>
                <DesktopProgress />
                {Object.keys(errors).length > 0 ? (
                    <div className={css.error_message_container}>
                        <span className={css.error_message}>
                            Please answer all the questions above to finish the test
                        </span>
                    </div>
                ) : (
                    <NavigationButton type="confirm" label={button} onClick={onSubmitForm} />
                )}
            </div>
        </div>
    )
}
